import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { API_BASE } from '../config';
import TokenUsageChart from '../components/common/TokenUsageChart';
import { BarChart3, AlertTriangle, AlertOctagon, ShieldCheck } from 'lucide-react';
import './Dashboard.css';

const Dashboard = () => {
  const { tenant, token } = useAuth();

  const [auditLogs, setAuditLogs] = useState([]);
  const [usageData, setUsageData] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({ actionType: '', from: '', to: '' });

  const [group, setGroup] = useState('week');
  const [chartType, setChartType] = useState('bar');
  const [usageFrom, setUsageFrom] = useState('');
  const [usageTo, setUsageTo] = useState('');

  useEffect(() => {
    const today = new Date();
    const monday = new Date(today);
    monday.setDate(today.getDate() - ((today.getDay() + 6) % 7));

    const format = (d) => d.toISOString().slice(0, 16);
    const from = format(monday);
    const to = format(today);

    setUsageFrom(from);
    setUsageTo(to);
    setFilters((prev) => ({ ...prev, from, to }));
  }, []);

  const formatDate = (iso) =>
    iso ? new Date(iso).toLocaleString() : '-';

  const fetchUsage = async () => {
    if (!usageFrom || !usageTo) return;
    const url = `${API_BASE}/dashboard/usage?group=${group}&from=${usageFrom}&to=${usageTo}`;
    const res = await fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) setUsageData(await res.json());
  };

  const fetchAuditLogs = async () => {
    const params = new URLSearchParams({ page, size: 10 });
    if (filters.actionType) params.append('actionType', filters.actionType);
    if (filters.from) params.append('from', filters.from);
    if (filters.to) params.append('to', filters.to);

    const res = await fetch(`${API_BASE}/dashboard/audit-logs?${params}`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (res.ok) {
      const data = await res.json();
      setAuditLogs(data.content || []);
      setTotalPages(data.totalPages || 1);
    }
  };

  const [exportFormat, setExportFormat] = useState('json');
  const [exportLoading, setExportLoading] = useState(false);
  const [exportSignature, setExportSignature] = useState('');
  const [exportCopied, setExportCopied] = useState(false);

  useEffect(() => {
    if (token && usageFrom && usageTo) fetchUsage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, group, usageFrom, usageTo]);

  useEffect(() => {
    if (token) fetchAuditLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, page]);

  const tokenLimit = tenant?.tokenLimitPerMonth || 10000;
  const tokensUsed = tenant?.tokensUsedThisMonth || 0;
  const detokenizationsUsed = tenant?.detokenizationsUsedThisMonth || 0;
  const usagePercent = Math.min(100, Math.round((tokensUsed / (tokenLimit || 1)) * 100));
  const tokensRemaining = Math.max(0, tokenLimit - tokensUsed);

  let quotaSeverity = 'normal';
  let quotaBadgeText = 'Healthy';
  let quotaMessage = 'System operating comfortably within provisioned capacity headroom.';
  let gaugeColor = '#10b981';
  let QuotaStatusIcon = ShieldCheck;

  if (usagePercent >= 95) {
    quotaSeverity = 'critical';
    quotaBadgeText = 'Critical (95%+)';
    quotaMessage = 'Warning: Monthly token allocation nearly depleted. Detokenization throttles will activate upon reaching 100%.';
    gaugeColor = '#ef4444';
    QuotaStatusIcon = AlertOctagon;
  } else if (usagePercent >= 80) {
    quotaSeverity = 'warning';
    quotaBadgeText = 'Warning (80%+)';
    quotaMessage = 'High volume detected. Quota consumption exceeds 80% of tier limit. Monitor active ingestion workloads.';
    gaugeColor = '#f59e0b';
    QuotaStatusIcon = AlertTriangle;
  }

  const handleExportCompliancePack = async () => {
    setExportLoading(true);
    setExportSignature('');
    try {
      const res = await fetch(`${API_BASE}/dashboard/audit-logs/export-report?format=${exportFormat}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || `Export failed with HTTP ${res.status}`);
      }
      const sig = res.headers.get('X-Audit-Signature') || 'sha256=verified_on_download';
      setExportSignature(sig);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `vaultize_qsa_compliance_pack_${new Date().toISOString().slice(0, 10)}.${exportFormat}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Compliance export error:', err);
      alert(`Failed to export compliance pack: ${err.message}`);
    } finally {
      setExportLoading(false);
    }
  };

  const copySignature = () => {
    if (!exportSignature) return;
    navigator.clipboard.writeText(exportSignature);
    setExportCopied(true);
    setTimeout(() => setExportCopied(false), 2500);
  };

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h2>Welcome back</h2>
        <p className="subtext">{tenant?.email}</p>
      </div>

      <div className="kpi-grid">
        <div className="kpi-card">
          <span className="kpi-label">Plan</span>
          <span className="kpi-value">{tenant?.plan}</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Token Limit</span>
          <span className="kpi-value">{tenant?.tokenLimitPerMonth}</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Used This Month</span>
          <span className="kpi-value">{tenant?.tokensUsedThisMonth}</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Detokenizations</span>
          <span className="kpi-value">{tenant?.detokenizationsUsedThisMonth}</span>
        </div>
      </div>

      {/* Visual Quota Exhaustion Gauge */}
      <div className={`quota-gauge-card quota-${quotaSeverity}`}>
        <div className="quota-gauge-header">
          <div className="quota-title-area">
            <span className="quota-icon">
              <BarChart3 size={18} aria-hidden="true" />
            </span>
            <div>
              <h4 className="quota-title">Monthly Quota Consumption &amp; Forecast</h4>
              <p className="quota-subtext">{quotaMessage}</p>
            </div>
          </div>
          <div className={`quota-status-badge badge-${quotaSeverity}`}>
            <QuotaStatusIcon size={13} style={{ marginRight: 6 }} aria-hidden="true" />
            <span>{quotaBadgeText}</span>
          </div>
        </div>

        <div className="quota-progress-container">
          <div className="quota-progress-track">
            <div
              className="quota-progress-fill"
              style={{
                width: `${usagePercent}%`,
                backgroundColor: gaugeColor
              }}
            />
            <div className="quota-threshold-marker marker-80" title="80% Warning Threshold">
              <span>80%</span>
            </div>
            <div className="quota-threshold-marker marker-95" title="95% Critical Ceiling">
              <span>95%</span>
            </div>
          </div>
        </div>

        <div className="quota-metrics-row">
          <div className="quota-metric-item">
            <span className="metric-title">Capacity Utilized</span>
            <span className="metric-val" style={{ color: gaugeColor }}>{usagePercent}%</span>
          </div>
          <div className="quota-metric-item">
            <span className="metric-title">Tokens Consumed</span>
            <span className="metric-val">{tokensUsed.toLocaleString()}</span>
          </div>
          <div className="quota-metric-item">
            <span className="metric-title">Remaining Headroom</span>
            <span className="metric-val">{tokensRemaining.toLocaleString()} tokens</span>
          </div>
          <div className="quota-metric-item">
            <span className="metric-title">Detokenizations Active</span>
            <span className="metric-val">{detokenizationsUsed.toLocaleString()}</span>
          </div>
        </div>
      </div>

      <div className="dashboard-section">
        <div className="section-header">
          <h3>Token Usage</h3>
        </div>

        <div className="chart-controls">
          <label>
            From
            <input type="datetime-local" value={usageFrom} onChange={(e) => setUsageFrom(e.target.value)} />
          </label>
          <label>
            To
            <input type="datetime-local" value={usageTo} onChange={(e) => setUsageTo(e.target.value)} />
          </label>
          <select value={group} onChange={(e) => setGroup(e.target.value)}>
            <option value="day">Daily</option>
            <option value="week">Weekly</option>
            <option value="month">Monthly</option>
          </select>
          <select value={chartType} onChange={(e) => setChartType(e.target.value)}>
            <option value="line">Line</option>
            <option value="bar">Bar</option>
            <option value="area">Area</option>
            <option value="composed">Composed</option>
            <option value="radar">Radar</option>
          </select>
        </div>

        <div className="chart-box">
          <TokenUsageChart usageData={usageData} chartType={chartType} />
        </div>
      </div>

      <div className="dashboard-section">
        <div className="section-header compliance-export-header">
          <div>
            <h3>Audit Trail & Compliance Records</h3>
            <p className="section-subtitle">Real-time immutable access logs signed with cryptographic HMAC verification</p>
          </div>
          <div className="compliance-export-controls">
            <select
              value={exportFormat}
              onChange={(e) => setExportFormat(e.target.value)}
              className="export-format-select"
              title="Select export format"
            >
              <option value="json">Signed JSON (.json)</option>
              <option value="csv">Compliance CSV (.csv)</option>
            </select>
            <button
              className="btn-compliance-export"
              onClick={handleExportCompliancePack}
              disabled={exportLoading}
            >
              {exportLoading ? '⏳ Generating Pack...' : '📋 Export QSA Compliance Pack'}
            </button>
          </div>
        </div>

        {exportSignature && (
          <div className="compliance-signature-banner">
            <div className="sig-content">
              <span className="sig-badge">✓ Tamper-Evident HMAC Verified</span>
              <span className="sig-text">
                Audit Digest: <code>{exportSignature}</code>
              </span>
            </div>
            <div className="sig-actions">
              <button className="sig-copy-btn" onClick={copySignature}>
                {exportCopied ? '✓ Copied!' : 'Copy Hash'}
              </button>
              <button className="sig-close-btn" onClick={() => setExportSignature('')}>✕</button>
            </div>
          </div>
        )}

        <div className="filters">
          <input
            placeholder="Action type"
            value={filters.actionType}
            onChange={(e) => setFilters({ ...filters, actionType: e.target.value })}
          />
          <input
            type="datetime-local"
            value={filters.from}
            onChange={(e) => setFilters({ ...filters, from: e.target.value })}
          />
          <input
            type="datetime-local"
            value={filters.to}
            onChange={(e) => setFilters({ ...filters, to: e.target.value })}
          />
          <button onClick={fetchAuditLogs}>Apply</button>
        </div>

        <table className="audit-table">
          <thead>
            <tr>
              <th>Action</th>
              <th>PII</th>
              <th>Token</th>
              <th>Date</th>
              <th>IP</th>
            </tr>
          </thead>
          <tbody>
            {auditLogs.map((log, i) => (
              <tr key={i}>
                <td>{log.actionType}</td>
                <td>{log.piiType}</td>
                <td>{log.token}</td>
                <td>{formatDate(log.timestamp)}</td>
                <td>{log.ipAddress || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="pagination">
          <button disabled={page === 0} onClick={() => setPage((p) => p - 1)}>Prev</button>
          <span>{page + 1} / {totalPages}</span>
          <button disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</button>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
